// Admin mutations. Each hook calls the API directly via `getApiClient()`
// and patches the QueryClient cache. Server actions are not involved; this
// is the canonical mutation pattern for the admin surface.

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import type { PlatformRole } from '@leanin/shared/views/profile';
import type { ImageCropRect } from '@/components/media/ImageCropDialog';
import { trackBroadcastSent } from '../analytics/events';
import { getApiClient } from '../api-client';
import { unwrap, unwrapVoid } from '../api-error';
import { adminKeys } from '../queries/admin';
import {
  broadcastKeys,
  type AdminBroadcastDetail,
  type BroadcastDoc,
  type BroadcastPreview,
  type BroadcastScope,
} from '../queries/broadcasts';
import {
  planBroadcastCreate,
  type CreateBroadcastInput,
} from './broadcast-create-plan';

type AudienceSpec = components['schemas']['AudienceSpec'];
type SendVia = 'email' | 'email_in_app';

// ─── Members ────────────────────────────────────────────────────────────

function invalidateMembersAndDetail(
  qc: ReturnType<typeof useQueryClient>,
  userId?: string,
) {
  qc.invalidateQueries({ queryKey: adminKeys.members() });
  if (userId !== undefined) {
    qc.invalidateQueries({ queryKey: adminKeys.memberDetail(userId).queryKey });
  }
}

export function useDeactivateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; reason: string }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/members/{id}/deactivate', {
          params: { path: { id: input.userId } },
          body: { reason: input.reason },
        }),
      );
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useReactivateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; reason: string }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/members/{id}/reactivate', {
          params: { path: { id: input.userId } },
          body: { reason: input.reason },
        }),
      );
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useBanMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; reason: string }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/members/{id}/ban', {
          params: { path: { id: input.userId } },
          body: { reason: input.reason },
        }),
      );
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useSetMemberPlatformRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      platformRole: PlatformRole | null;
    }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/members/{id}/platform-role', {
          params: { path: { id: input.userId } },
          body: { platformRole: input.platformRole },
        }),
      );
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useSetColdStartSeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; enabled: boolean }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/members/{id}/cold-start-seed', {
          params: { path: { id: input.userId } },
          body: { enabled: input.enabled },
        }),
      );
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export interface UpdateMemberProfileInput {
  readonly userId: string;
  readonly firstName?: string;
  readonly lastName?: string | null;
  readonly email?: string;
  readonly bio?: string | null;
  readonly jobTitle?: string | null;
  readonly company?: string | null;
  readonly industry?: string | null;
  /** Structured place payload from `PlacePicker`. `null` clears any prior
   *  selection. */
  readonly place?: {
    placeId: string;
    placeText: string;
    placeLat: number;
    placeLng: number;
    placeCountryCode: string | null;
  } | null;
  readonly secondaryEmail?: string | null;
  readonly timezone?: string | null;
}

export function useUpdateMemberProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateMemberProfileInput) => {
      const { userId, ...rest } = input;
      const client = getApiClient();
      unwrapVoid(
        await client.PATCH('/api/v1/admin/members/{id}/profile', {
          params: { path: { id: userId } },
          body: rest,
        }),
      );
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useBulkSetMemberPlatformRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userIds: ReadonlyArray<string>;
      platformRole: PlatformRole | null;
    }) => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/admin/members/bulk/platform-role', {
          body: {
            userIds: [...input.userIds],
            platformRole: input.platformRole,
          },
        }),
      );
      return {
        successCount: data.successCount,
        failureCount: data.failures.length,
      };
    },
    onSuccess: () => invalidateMembersAndDetail(qc),
  });
}

export function useBulkAddToCircle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userIds: ReadonlyArray<string>;
      circleId: string;
    }) => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/admin/members/bulk/circle', {
          body: { userIds: [...input.userIds], circleId: input.circleId },
        }),
      );
      return {
        successCount: data.successCount,
        failureCount: data.failures.length,
      };
    },
    onSuccess: () => invalidateMembersAndDetail(qc),
  });
}

export function useBulkDeactivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userIds: ReadonlyArray<string>;
      reason: string;
    }) => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/admin/members/bulk/deactivate', {
          body: { userIds: [...input.userIds], reason: input.reason },
        }),
      );
      return {
        successCount: data.successCount,
        failureCount: data.failureCount,
      };
    },
    onSuccess: () => invalidateMembersAndDetail(qc),
  });
}

export function useBulkSendEmail() {
  return useMutation({
    mutationFn: async (input: {
      userIds: ReadonlyArray<string>;
      subject: string;
      bodyMarkdown: string;
    }) => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/admin/members/bulk/email', {
          body: {
            userIds: [...input.userIds],
            subject: input.subject,
            body: input.bodyMarkdown,
          },
        }),
      );
      return {
        draftId: data.draftId,
        recipientCount: data.recipientCount,
      };
    },
  });
}

export function useAddMemberToCircle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; circleId: string }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/members/{userId}/circles', {
          params: { path: { userId: input.userId } },
          body: { circleId: input.circleId },
        }),
      );
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useRemoveMemberFromCircle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; circleId: string }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE(
          '/api/v1/admin/members/{userId}/circles/{circleId}',
          {
            params: {
              path: { userId: input.userId, circleId: input.circleId },
            },
          },
        ),
      );
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useAddMemberToNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; networkId: string }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/members/{userId}/networks', {
          params: { path: { userId: input.userId } },
          body: { networkId: input.networkId },
        }),
      );
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useRemoveMemberFromNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; networkId: string }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE(
          '/api/v1/admin/members/{userId}/networks/{networkId}',
          {
            params: {
              path: { userId: input.userId, networkId: input.networkId },
            },
          },
        ),
      );
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export type PresignPhotoResult = {
  uploadUrl: string;
  fields: Record<string, string>;
  key: string;
};

export function usePresignMemberPhoto() {
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      contentType: 'image/jpeg' | 'image/png' | 'image/webp';
    }): Promise<PresignPhotoResult> => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/admin/members/{id}/photo', {
          params: { path: { id: input.userId } },
          body: { contentType: input.contentType },
        }),
      );
      return {
        uploadUrl: data.uploadUrl,
        fields: data.fields,
        key: data.key,
      };
    },
  });
}

export function useCommitMemberPhoto() {
  const qc = useQueryClient();
  return useMutation({
    // `originalStorageKey`/`crop` are optional on the wire but required
    // here: committing without them clears the server-retained original.
    mutationFn: async (input: {
      userId: string;
      storageKey: string;
      originalStorageKey: string;
      crop: ImageCropRect;
    }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/members/{id}/photo/commit', {
          params: { path: { id: input.userId } },
          body: {
            storageKey: input.storageKey,
            originalStorageKey: input.originalStorageKey,
            crop: input.crop,
          },
        }),
      );
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useSendPasswordResetEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/members/{id}/password-reset', {
          params: { path: { id: userId } },
        }),
      );
    },
    onSuccess: (_data, userId) =>
      invalidateMembersAndDetail(qc, userId),
  });
}

// ─── Invitations ─────────────────────────────────────────────────────────

export interface CreateInvitationInput {
  email: string;
  firstName: string;
  lastName: string;
  /** Optional platform grant applied at acceptance time. Omit (or pass
   *  `null`) to invite a regular user. */
  platformRole?: PlatformRole | null;
  addToCircleId?: string;
  addToNetworkId?: string;
  jobTitle?: string;
  company?: string;
  personalNote?: string;
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateInvitationInput) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/invitations', {
          body: {
            email: input.email,
            firstName: input.firstName,
            lastName: input.lastName,
            ...(input.platformRole !== undefined
              ? { platformRole: input.platformRole }
              : {}),
            ...(input.addToCircleId !== undefined
              ? { addToCircleId: input.addToCircleId }
              : {}),
            ...(input.addToNetworkId !== undefined
              ? { addToNetworkId: input.addToNetworkId }
              : {}),
            ...(input.jobTitle !== undefined
              ? { jobTitle: input.jobTitle }
              : {}),
            ...(input.company !== undefined
              ? { company: input.company }
              : {}),
            ...(input.personalNote !== undefined
              ? { personalNote: input.personalNote }
              : {}),
          },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

type ReferralLink = components['schemas']['ReferralLink'];

export function useCreateReferralLink(): UseMutationResult<
  ReferralLink,
  Error,
  { title: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title: string }) => {
      const client = getApiClient();
      return unwrap(
        await client.POST('/api/v1/admin/referral-links', {
          body: { title: input.title },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export function useDeactivateReferralLink(): UseMutationResult<
  void,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/referral-links/{id}/deactivate', {
          params: { path: { id } },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/invitations/{id}/revoke', {
          params: { path: { id: invitationId } },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/invitations/{id}/resend', {
          params: { path: { id: invitationId } },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export interface BulkInviteCreatedRow {
  email: string;
  firstName: string | null;
  lastName: string | null;
  invitationId: string;
}

export interface BulkInviteSkippedRow {
  row: number;
  email: string;
  reason: string;
}

export function useBulkInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (csv: string) => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/admin/invitations/bulk', {
          body: { csv },
        }),
      );
      return {
        created: data.invitations.map(
          (r): BulkInviteCreatedRow => ({
            email: r.email,
            firstName: r.firstName ?? null,
            lastName: r.lastName ?? null,
            invitationId: r.invitationId,
          }),
        ),
        skipped: data.skipped.map(
          (r): BulkInviteSkippedRow => ({
            row: r.row,
            email: r.email,
            reason: r.reason,
          }),
        ),
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

// ─── Broadcasts ──────────────────────────────────────────────────────────
//
// Two URL families:
//   • Per-scope create + list + detail under `/admin/broadcasts` (platform)
//     and `/circles|networks|spaces/{id}/broadcasts` (groups). Scope IS the
//     lookup key, so create stays scope-aware here.
//   • Canonical operations on an existing row — PATCH, /preview, DELETE,
//     /duplicate — live at `/api/v1/broadcasts/{id}` regardless of scope.
//     Path B authz resolves scope from the broadcast row.
//
// Cache invalidation (Frozen Decision 11): every mutation that touches an
// existing row writes the detail back via `setQueryData(detail(scope, id))`
// then invalidates the list + statusCounts prefixes for that scope. For
// platform-only hooks scope is `{kind: 'platform'}`; the group hooks live in
// mutations/groups.ts and carry kind/groupId so they can target the right
// scope key.

// `CreateBroadcastInput` and the send/draft endpoint routing live in
// ./broadcast-create-plan (a pure, tested decision). Re-exported here so
// existing `@/lib/mutations/admin` importers keep their path.
export type { CreateBroadcastInput };

// Prefix that matches every cached list for a scope regardless of params.
// `broadcastKeys.list(scope, _)` is `[...scope, 'list', params]` — strip
// the trailing params object to invalidate "any list under this scope".
function listPrefix(scope: BroadcastScope) {
  return [...broadcastKeys.scope(scope), 'list'] as const;
}

const PLATFORM_SCOPE: BroadcastScope = { kind: 'platform' };

function invalidatePlatformBroadcasts(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: listPrefix(PLATFORM_SCOPE) });
  qc.invalidateQueries({
    queryKey: broadcastKeys.statusCounts(PLATFORM_SCOPE).queryKey,
  });
}

export function useCreateBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBroadcastInput) => {
      const client = getApiClient();
      // draft → /create (no step-up); send / schedule → /admin/broadcasts
      // (step-up gated). See ./broadcast-create-plan for the full rationale.
      const plan = planBroadcastCreate(input);
      if (plan.endpoint === '/api/v1/admin/broadcasts') {
        unwrapVoid(
          await client.POST('/api/v1/admin/broadcasts', {
            body: plan.body,
          }),
        );
        return;
      }
      unwrapVoid(
        await client.POST('/api/v1/admin/broadcasts/create', {
          body: plan.body,
        }),
      );
    },
    onSuccess: (_data, vars) => {
      if (vars.mode === 'send') {
        trackBroadcastSent({
          audience_type: vars.audienceSpec.kind,
          send_via: vars.sendVia,
        });
      }
      // Legacy admin-keyed broadcast caches still exist for the platform
      // list page (queries/admin.ts `useAdminBroadcasts`). Invalidate both
      // surfaces until that page migrates to the scope-aware reader.
      qc.invalidateQueries({ queryKey: adminKeys.broadcasts() });
      invalidatePlatformBroadcasts(qc);
    },
  });
}

export interface UpdateBroadcastInput {
  version: number;
  sendVia?: SendVia;
  subject?: string;
  bodyDoc?: BroadcastDoc;
  preheaderText?: string | null;
  replyTo?: string | null;
  audienceSpec?: AudienceSpec;
  scheduledSendAt?: string | null;
}

export function useUpdateBroadcast(): UseMutationResult<
  AdminBroadcastDetail,
  Error,
  { id: string; patch: UpdateBroadcastInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => {
      const client = getApiClient();
      const data = unwrap(
        await client.PATCH('/api/v1/broadcasts/{id}', {
          params: { path: { id } },
          body: {
            version: patch.version,
            ...(patch.sendVia !== undefined ? { sendVia: patch.sendVia } : {}),
            ...(patch.subject !== undefined ? { subject: patch.subject } : {}),
            ...(patch.bodyDoc !== undefined ? { bodyDoc: patch.bodyDoc } : {}),
            ...(patch.preheaderText !== undefined
              ? { preheaderText: patch.preheaderText }
              : {}),
            ...(patch.replyTo !== undefined ? { replyTo: patch.replyTo } : {}),
            ...(patch.audienceSpec !== undefined
              ? { audienceSpec: patch.audienceSpec }
              : {}),
            ...(patch.scheduledSendAt !== undefined
              ? { scheduledSendAt: patch.scheduledSendAt }
              : {}),
          },
        }),
      );
      return data.data;
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(broadcastKeys.detail(PLATFORM_SCOPE, vars.id).queryKey, data);
      invalidatePlatformBroadcasts(qc);
      // Keep the legacy admin-broadcasts list (queries/admin.ts) in sync
      // until that surface migrates to the scope-aware reader.
      qc.invalidateQueries({ queryKey: adminKeys.broadcasts() });
    },
  });
}

export interface PreviewBroadcastInput {
  id: string;
  bodyDoc?: BroadcastDoc;
  subject?: string;
  preheaderText?: string | null;
  firstName?: string;
}

export function usePreviewBroadcast(): UseMutationResult<
  BroadcastPreview,
  Error,
  PreviewBroadcastInput
> {
  return useMutation({
    mutationFn: async (input) => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/broadcasts/{id}/preview', {
          params: { path: { id: input.id } },
          body: {
            ...(input.bodyDoc !== undefined ? { bodyDoc: input.bodyDoc } : {}),
            ...(input.subject !== undefined ? { subject: input.subject } : {}),
            ...(input.preheaderText !== undefined
              ? { preheaderText: input.preheaderText }
              : {}),
            ...(input.firstName !== undefined
              ? { firstName: input.firstName }
              : {}),
          },
        }),
      );
      return data.data;
    },
  });
}

export function useDuplicateBroadcast(): UseMutationResult<
  { id: string },
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (broadcastId) => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/broadcasts/{id}/duplicate', {
          params: { path: { id: broadcastId } },
        }),
      );
      return { id: data.id };
    },
    onSuccess: () => {
      // The duplicated row's scope matches the source row's scope, which the
      // caller doesn't pass through this signature. Blanket-invalidate every
      // broadcast list/statusCounts entry so the new row appears wherever
      // it lives.
      qc.invalidateQueries({ queryKey: broadcastKeys.all });
      qc.invalidateQueries({ queryKey: adminKeys.broadcasts() });
    },
  });
}

export function useDeleteBroadcast(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (broadcastId) => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE('/api/v1/broadcasts/{id}', {
          params: { path: { id: broadcastId } },
        }),
      );
    },
    onSuccess: (_data, broadcastId) => {
      // Drop the now-gone broadcast's detail cache entirely (across every
      // scope variant the key cartesian could hold) so the detail view's
      // useQuery doesn't refetch a 404 after the row's been deleted.
      // `removeQueries` evicts the cache without triggering a refetch;
      // `invalidateQueries` would re-fire and log a 404 in the console.
      qc.removeQueries({
        predicate: (q) =>
          q.queryKey[0] === 'broadcasts' &&
          q.queryKey.includes('detail') &&
          q.queryKey.includes(broadcastId),
      });
      // Lists + status-counts across every scope DO need re-fetching.
      qc.invalidateQueries({ queryKey: broadcastKeys.all });
      qc.invalidateQueries({ queryKey: adminKeys.broadcasts() });
    },
  });
}

export function useRescheduleBroadcast(): UseMutationResult<
  void,
  Error,
  { id: string; scheduledAt: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, scheduledAt }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/broadcasts/{id}/schedule', {
          params: { path: { id } },
          body: { scheduledAt },
        }),
      );
    },
    onSuccess: () => {
      // Detail timestamp + status pill + list ordering all read off the
      // broadcast row, so invalidate the union.
      qc.invalidateQueries({ queryKey: broadcastKeys.all });
      qc.invalidateQueries({ queryKey: adminKeys.broadcasts() });
    },
  });
}

// ─── User research ───────────────────────────────────────────────────────

export type InterviewInviteContent =
  components['schemas']['AdminInterviewInviteContent'];
export type InterviewRecipientOutcome =
  components['schemas']['AdminInterviewRecipientOutcome'];

export interface SendInterviewInvitesInput {
  readonly userIds: ReadonlyArray<string>;
  readonly content: InterviewInviteContent;
  readonly allowRecontact?: boolean;
}

// Step-up gated server-side: call sites must run `mutateAsync` through
// `runStepUp` from `use-step-up` so the Clerk reverification modal opens
// (and the send retries once) when the session is outside its step-up
// window.
export function useSendInterviewInvites(): UseMutationResult<
  components['schemas']['AdminInterviewSendInvitesResponse'],
  Error,
  SendInterviewInvitesInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const client = getApiClient();
      return unwrap(
        await client.POST('/api/v1/admin/user-research/invites', {
          body: {
            userIds: [...input.userIds],
            content: input.content,
            ...(input.allowRecontact !== undefined
              ? { allowRecontact: input.allowRecontact }
              : {}),
          },
        }),
      );
    },
    onSuccess: () => {
      // Contacted users drop out of the default candidate list — refetch so
      // the table reflects who is still reachable.
      qc.invalidateQueries({ queryKey: adminKeys.userResearch() });
    },
  });
}

// Renders the invite email exactly as a recipient will see it. Admin-only
// but not step-up gated — it sends nothing.
export function usePreviewInterviewInvite(): UseMutationResult<
  components['schemas']['AdminInterviewPreviewResponse'],
  Error,
  { content: InterviewInviteContent; sampleFirstName: string }
> {
  return useMutation({
    mutationFn: async (input) => {
      const client = getApiClient();
      return unwrap(
        await client.POST('/api/v1/admin/user-research/invites/preview', {
          body: {
            content: input.content,
            sampleFirstName: input.sampleFirstName,
          },
        }),
      );
    },
  });
}

// ─── Moderation ──────────────────────────────────────────────────────────

type ModerationAction =
  | 'manual_hide'
  | 'delete'
  | 'release'
  | 'warn'
  | 'suspend'
  | 'reactivate'
  | 'ban';

function invalidateModerationFor(
  qc: ReturnType<typeof useQueryClient>,
  reportId?: string,
) {
  qc.invalidateQueries({ queryKey: adminKeys.moderation() });
  if (reportId !== undefined) {
    qc.invalidateQueries({ queryKey: adminKeys.moderationReport(reportId).queryKey });
  }
}

// One decision can carry up to two actions (at most one content-level + one
// member-level, e.g. delete + ban); the server applies the set in one
// transaction. Order in `actions` is content-level first — the caller builds it
// from the DECISIONS table order.
export function useApplyModerationActions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      reportId: string;
      actions: readonly ModerationAction[];
      reason: string;
      notifyReporter: boolean;
      reporterMessage?: string;
    }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/moderation/reports/{id}/action', {
          params: { path: { id: input.reportId } },
          body: {
            actions: [...input.actions],
            reason: input.reason,
            notifyReporter: input.notifyReporter,
            ...(input.reporterMessage !== undefined
              ? { reporterMessage: input.reporterMessage }
              : {}),
          },
        }),
      );
    },
    onSuccess: (_data, vars) => invalidateModerationFor(qc, vars.reportId),
  });
}

export function useDismissReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      reportId: string;
      notes?: string;
      notifyReporter: boolean;
      reporterMessage?: string;
    }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/moderation/reports/{id}/dismiss', {
          params: { path: { id: input.reportId } },
          body: {
            ...(input.notes !== undefined ? { notes: input.notes } : {}),
            notifyReporter: input.notifyReporter,
            ...(input.reporterMessage !== undefined
              ? { reporterMessage: input.reporterMessage }
              : {}),
          },
        }),
      );
    },
    onSuccess: (_data, vars) => invalidateModerationFor(qc, vars.reportId),
  });
}

export function useEscalateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reportId: string; note: string }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/moderation/reports/{id}/escalate', {
          params: { path: { id: input.reportId } },
          body: { note: input.note },
        }),
      );
    },
    onSuccess: (_data, vars) => invalidateModerationFor(qc, vars.reportId),
  });
}

export function useConfirmHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (holdId: string) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/moderation/holds/{id}/confirm', {
          params: { path: { id: holdId } },
        }),
      );
    },
    onSuccess: () => invalidateModerationFor(qc),
  });
}

export function useReleaseHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { holdId: string; reason: string }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/moderation/holds/{id}/release', {
          params: { path: { id: input.holdId } },
          body: { reason: input.reason },
        }),
      );
    },
    onSuccess: () => invalidateModerationFor(qc),
  });
}

export function useFinalizeDeleteHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { holdId: string; reason: string }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST(
          '/api/v1/admin/moderation/holds/{id}/finalize-delete',
          {
            params: { path: { id: input.holdId } },
            body: { reason: input.reason },
          },
        ),
      );
    },
    onSuccess: () => invalidateModerationFor(qc),
  });
}

export function useResolveContestation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      contestationId: string;
      outcome: 'upheld' | 'overturned';
      reason: string;
    }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST(
          '/api/v1/admin/moderation/contestations/{id}/resolve',
          {
            params: { path: { id: input.contestationId } },
            body: { outcome: input.outcome, reason: input.reason },
          },
        ),
      );
    },
    onSuccess: () => invalidateModerationFor(qc),
  });
}
